// src/components/screens/agent/FeeSummaryPdf.jsx
import React, { forwardRef, useState, useEffect } from "react";
import { getBase64Image } from "../../../utils/imageUtils";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Divider,
} from "@mui/material";

const FeeSummaryPdf = forwardRef(
  (
    {
      totalFee,
      selectedCurrency,
      convertCurrency,
      getCurrencySymbol,
      feeData,
      formState,
      durationOptions,
      getFeeAmount,
      currencyOptions,
    },
    ref
  ) => {
    const [logoBase64, setLogoBase64] = useState("");

    useEffect(() => {
      getBase64Image("/assets/images/osa-logo.svg").then((base64) => {
        setLogoBase64(base64);
      });
    }, []);

    return (
      <Box
        ref={ref}
        sx={{
          width: "794px", // A4 width 
          padding: "2rem 3rem",
          background: "#fff",
          color: "#000",
          fontFamily: "Arial, sans-serif",
        }}
      >
        {/* Header with logo and title */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          mb={2}
          gap={2}
        >
          {logoBase64 && (
            <img
              src={logoBase64}
              alt="OSA Private School Logo"
              style={{
                width: 220,
                height: 120,
                objectFit: "contain",
              }}
            />
          )}
        </Box>

        {/* Currency and Fee Info Row */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mt={4}
          mb={2}
          flexWrap="wrap"
          gap={2}
        >
          {/* Currency Selector */}
          <Box>
            <Typography variant="caption" color="var(--secondary)">
              Currency
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {currencyOptions.find((c) => c.value === selectedCurrency)
                ?.label || selectedCurrency}
            </Typography>
          </Box>

          {/* Total and Estimated Fees */}
          <Box textAlign="right">
            <Typography variant="h6" fontWeight="bold">
              CAD ${totalFee.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="var(--primary)">
              Base total in CAD
            </Typography>
            {selectedCurrency !== "CAD" && (
              <>
                <Typography variant="body1" fontWeight="500">
                  {getCurrencySymbol()}
                  {convertCurrency(totalFee).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Estimated in {selectedCurrency}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Breakdown Section */}
        <Typography
          variant="h6"
          fontWeight="bold"
          color="var(--secondary)"
          gutterBottom
        >
          Breakdown
        </Typography>

        {feeData?.feeCategories
          .slice()
          .sort((a, b) => {
            const priority = ["Tuition", "Homestay", "Uniform"];
            const aIndex = priority.includes(a.name)
              ? priority.indexOf(a.name)
              : 99;
            const bIndex = priority.includes(b.name)
              ? priority.indexOf(b.name)
              : 99;
            return aIndex - bIndex;
          })
          .map((category) => {
            const catState = formState[category.name];
            if (!catState?.enabled && category.type === "add-on") return null;

            if (category.hasSubCategory) {
              return (feeData.subCategoriesByParent[category.name] || []).map(
                (sub) => {
                  const durationId = catState?.subDurations?.[sub.name];
                  if (!durationId) return null;
                  const durationLabel =
                    durationOptions.find((d) => d.value === durationId)
                      ?.label || "";
                  const rawAmount = getFeeAmount(category.name, durationId);
                  const convertedAmount =
                    convertCurrency(rawAmount)?.toFixed(2);

                  return (
                    <Typography
                      key={`${category.name}-${sub.name}`}
                      variant="body2"
                      sx={{ mb: 0.5 }}
                    >
                      <strong>
                        {category.name} → {sub.name}
                      </strong>
                      : {durationLabel} — {getCurrencySymbol()}
                      {convertedAmount}
                    </Typography>
                  );
                }
              );
            } else {
              const durationId = catState?.durationId;
              if (!durationId) return null;

              const durationLabel =
                durationOptions.find((d) => d.value === durationId)?.label ||
                "";
              const rawAmount = getFeeAmount(category.name, durationId);
              const convertedAmount = convertCurrency(rawAmount).toFixed(2);

              return (
                <Typography
                  key={category.name}
                  variant="body2"
                  sx={{ mb: 0.5 }}
                >
                  <strong>{category.name}</strong>: {durationLabel} —{" "}
                  {getCurrencySymbol()}
                  {convertedAmount}
                </Typography>
              );
            }
          })}

        {/* Notes Section */}
        <Box mt={4}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            color="var(--secondary)"
            gutterBottom
          >
            Important to note:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 0, mt: 1 }}>
            <li>
              <Typography variant="body2" component="span" sx={{ pl: 1 }}>
                All fees are invoiced in CAD.
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span" sx={{ pl: 1 }}>
                We do not invoice for Homestay fees, so all fees are an estimate
                and you will need to confirm with Canada Homestay for firm confirmation. <strong>Email:</strong> help@canadahomestaynetwork.ca
              </Typography>
            </li>
          </Box>
        </Box>
      </Box>
    );
  }
);

export default FeeSummaryPdf;
